/**
 * Tracking Manager
 * Controls enabling/disabling tracking scripts based on privacy opt-out status
 * Works in conjunction with PrivacyOptOut module
 */
(() => {
  'use strict';

  // Stand-ins installed while a category is blocked. Named so a same-page
  // accept can tell its own stubs apart from a real tracker before undoing them.
  const blockedGa = function () {};
  const blockedFbq = function () {};

  const TrackingManager = {
    isInitialized: false,
    trackingEnabled: true,
    blockedScripts: [],
    revokedFbq: null, // real fbq saved by disableFacebookPixel()
    firstGrantAt: null, // ms since navigation start; read by consent-debug.js
    firstGrantTrigger: null, // what caused that grant; ditto

    /**
     * Initialize the tracking manager
     */
    init() {
      if (this.isInitialized) return;
      this.isInitialized = true;

      // Native consent gating (initNativeConsent) is NOT started here -- it
      // starts at script load, see the bottom of this file.

      // Listen for privacy opt-out ready event
      document.addEventListener('privacyOptOutReady', (e) => {
        this.handlePrivacyStatus(e.detail);
      });

      // Listen for privacy opt-out changes
      document.addEventListener('privacyOptOutChange', (e) => {
        this.handlePrivacyChange(e.detail);
      });

      // Check if PrivacyOptOut already initialized
      if (window.PrivacyOptOut && window.PrivacyOptOut.isOptedOut !== undefined) {
        this.handlePrivacyStatus({
          isOptedOut: window.PrivacyOptOut.isOptedOut,
          hasGPCSignal: window.PrivacyOptOut.hasGPCSignal
        });
      }

      // Fallback: check localStorage directly (for pages without PrivacyOptOut module)
      if (!window.PrivacyOptOut) {
        try {
          const stored = localStorage.getItem('privacy_opt_out');
          if (stored) {
            const data = JSON.parse(stored);
            if (data.optedOut) {
              this.handlePrivacyStatus({ isOptedOut: true, hasGPCSignal: !!data.gpcDetected });
            }
          }
        } catch (e) {
          console.error('[TrackingManager] Error reading localStorage fallback:', e);
        }
      }

    },

    /**
     * Handle initial privacy status
     * @param {Object} detail - Privacy status details
     */
    handlePrivacyStatus(detail) {
      this.trackingEnabled = !detail.isOptedOut;

      if (detail.isOptedOut) {
        this.disableTracking();
      } else {
        this.enableTracking();
      }

    },

    /**
     * Handle privacy status change
     * @param {Object} detail - Change details
     */
    handlePrivacyChange(detail) {
      this.trackingEnabled = !detail.isOptedOut;

      if (detail.isOptedOut) {
        this.disableTracking();
      } else {
        this.enableTracking();
      }

    },

    /**
     * Subscribe to Shopify's native Customer Privacy API.
     *
     * `visitorConsentCollected` is a DOM event, so we can listen for it even
     * before the API object finishes loading. We also do an initial read once
     * the API is available so trackers are gated correctly on first paint.
     *
     * Shopify's native banner is enabled for every region, so
     * analyticsProcessingAllowed()/marketingAllowed() read "blocked" until the
     * visitor accepts, and stay blocked after a decline.
     */
    initNativeConsent() {
      // Re-evaluate whenever the visitor accepts/declines/changes their choice.
      document.addEventListener('visitorConsentCollected', () => {
        this.evaluateNativeConsent('decision');
      });

      // Our own opt-out record can flip back to opted-in AFTER the first
      // evaluation -- privacy-opt-out.js adopting a newer Shopify accept over a
      // stale stored decline, or the privacy page's opt-in button. The grant was
      // withheld while it read opted out, and nothing else re-grants, so a
      // consenting visitor would stay denied for the rest of the page view.
      document.addEventListener('privacyOptOutChange', (e) => {
        if (e.detail && e.detail.isOptedOut === false) this.evaluateNativeConsent('opt-out record flipped');
      });

      this.whenNativeApiReady(() => this.evaluateNativeConsent('page load'));
    },

    /**
     * Wait for window.Shopify.customerPrivacy to load, then run cb.
     *
     * Speed matters here for visitors who ALREADY accepted: Google starts each
     * page denied and gives an update only wait_for_update ms
     * (gtag-consent-defaults.liquid) after gtag.js loads before sending the
     * page's first hits. A grant that lands later doesn't resend them, so that
     * page view counts as unconsented. Hence a short fixed poll -- the old
     * backoff noticed the API up to ~2.5s after it loaded. Giving up (~27s)
     * leaves the page at the denied default.
     * @param {Function} cb
     */
    whenNativeApiReady(cb) {
      const POLL_MS = 50;
      const GIVE_UP_MS = 27500;
      const apiReady = () => typeof window.Shopify !== 'undefined' && !!window.Shopify.customerPrivacy;

      if (apiReady()) {
        cb();
        return;
      }

      const startedAt = Date.now();
      const timer = setInterval(() => {
        if (apiReady()) {
          clearInterval(timer);
          cb();
          return;
        }
        if (Date.now() - startedAt >= GIVE_UP_MS) clearInterval(timer);
      }, POLL_MS);
    },

    /**
     * Read the native consent decision and gate trackers per category.
     * Analytics consent gates GA; marketing consent gates ad pixels (Meta, Ads).
     * @param {string} trigger - why this ran; recorded with the first grant so
     *   consent-debug.js can tell a decision made on this page (where earlier
     *   unconsented hits are correct) from consent that existed at page load.
     */
    evaluateNativeConsent(trigger) {
      const cp = (typeof window.Shopify !== 'undefined' && window.Shopify.customerPrivacy) || null;
      if (!cp) return;

      // Treat a missing method as blocked: consent is required everywhere, so
      // never grant on a decision we can't actually read.
      const analyticsAllowed = typeof cp.analyticsProcessingAllowed === 'function'
        && cp.analyticsProcessingAllowed() === true;
      const marketingAllowed = typeof cp.marketingAllowed === 'function'
        && cp.marketingAllowed() === true;

      if (!analyticsAllowed) {
        this.disableGoogleAnalytics();
        this.disableYotpoTracking();
        this.disableWiedersehen();
      }

      // Grant is as important as deny: gtag-consent-defaults.liquid denies by
      // default for every visitor, so without an explicit grant a visitor who
      // ACCEPTS would stay denied for the whole session.
      // Only granted per-category, and never while our own opt-out state says
      // the visitor is out (GPC / server tag / stored decline).
      var locallyOptedOut = !!(window.PrivacyOptOut && window.PrivacyOptOut.isOptedOut);
      const grantsAnything = (analyticsAllowed || marketingAllowed) && !locallyOptedOut;
      if (grantsAnything && this.firstGrantAt === null) {
        this.firstGrantAt = Math.round(performance.now());
        this.firstGrantTrigger = trigger;
      }
      if (analyticsAllowed && !locallyOptedOut) {
        this.restoreAnalyticsTracking();
        this.pushConsent('update', { 'analytics_storage': 'granted' });
      }
      if (marketingAllowed && !locallyOptedOut) {
        this.restoreMarketingTracking();
        this.pushConsent('update', {
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted'
        });
      }

      if (!marketingAllowed) {
        this.disableFacebookPixel();
        // Deny Google Ads storage signals via Consent Mode. Sent through
        // pushConsent() so it works even before gtag.js has loaded.
        this.pushConsent('update', {
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied'
        });
      }

      // If either category is blocked, clear the global flag so ad-hoc tracking
      // calls guarded by TrackingManager.track()/isTrackingEnabled() also stop.
      // Re-derived on every evaluation (not just cleared) so a same-page accept
      // turns it back on.
      this.trackingEnabled = analyticsAllowed && marketingAllowed && !locallyOptedOut;
    },

    /**
     * Undo this file's same-page analytics blocks once the visitor accepts.
     *
     * Every first-time visitor starts blocked, so their landing page has
     * already been through disableGoogleAnalytics()/disableYotpoTracking()/
     * disableWiedersehen() by the time they click Accept. Without this, those
     * stay in force until the next page load. Only called from a grant, which
     * is never issued while the visitor is locally opted out.
     */
    restoreAnalyticsTracking() {
      window['ga-disable-' + (window.gaTrackingId || '')] = false;
      if (window.ga === blockedGa) delete window.ga;
      if (window.yotpo) window.yotpo.disableConversionTracking = false;
      window.__wiedersehenDisabled = false;
    },

    /**
     * Marketing counterpart of restoreAnalyticsTracking(): puts back the real
     * fbq that disableFacebookPixel() revoked, or removes the stub so a pixel
     * that loads later on this page can initialize normally.
     */
    restoreMarketingTracking() {
      if (window.fbq !== blockedFbq) return;

      if (!this.revokedFbq) {
        delete window.fbq;
        return;
      }

      window.fbq = this.revokedFbq;
      this.revokedFbq = null;
      window.fbq('consent', 'grant');
    },

    /**
     * Send a Google Consent Mode command.
     *
     * The old code wrapped these calls in `if (typeof window.gtag === 'function')`,
     * which is false whenever this file runs before gtag.js -- and it always
     * does: theme.liquid loads it synchronously above content_for_header, which
     * is where app embeds inject any Google tag -- so every consent denial
     * silently no-oped.
     *
     * The fix is to ensure the standard gtag shim exists and call it, rather than
     * skipping. Google requires the gtag() function interface -- a hand-rolled
     * dataLayer.push(['consent', ...]) does NOT drive its consent state. The shim
     * pushes the `arguments` object onto dataLayer, which the tag replays when it
     * initializes, so a call made before the tag loads is still honored.
     *
     * snippets/gtag-consent-defaults.liquid defines the same shim and the
     * denied 'default' before the tag; this handles later 'update' commands.
     *
     * @param {string} action - 'default' or 'update'
     * @param {Object} params - Consent Mode signal map
     */
    pushConsent(action, params) {
      if (!action || !params) return;
      try {
        window.dataLayer = window.dataLayer || [];
        if (typeof window.gtag !== 'function') {
          window.gtag = function () { window.dataLayer.push(arguments); };
        }
        window.gtag('consent', action, params);
      } catch (e) {
        console.error('[TrackingManager] Error sending consent signal:', e);
      }
    },

    /**
     * Deny every Google storage signal (ads + analytics).
     */
    denyGoogleConsent() {
      this.pushConsent('update', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'analytics_storage': 'denied'
      });
    },

    /**
     * Disable all tracking scripts
     */
    disableTracking() {
      // Disable Google Analytics
      this.disableGoogleAnalytics();

      // Disable Facebook Pixel
      this.disableFacebookPixel();

      // Disable Yotpo tracking (but keep reviews functional)
      this.disableYotpoTracking();

      // Disable wiedersehen analytics
      this.disableWiedersehen();

      // Set global flag for other scripts to check
      window.__trackingDisabled = true;

      // Clear any existing tracking cookies
      this.clearTrackingCookies();
    },

    /**
     * Enable tracking (only works if not opted out)
     */
    enableTracking() {
      if (window.PrivacyOptOut && window.PrivacyOptOut.isOptedOut) {
        return;
      }

      window.__trackingDisabled = false;
    },

    /**
     * Disable Google Analytics
     */
    disableGoogleAnalytics() {
      // Disable GA4
      window['ga-disable-' + (window.gaTrackingId || '')] = true;

      // Opt out of GA
      if (typeof window.gaOptout === 'function') {
        window.gaOptout();
      }

      // Deny all Google storage signals. Sent through pushConsent() so it is
      // honored even though gtag.js loads after this file.
      this.denyGoogleConsent();

      // Prevent future GA calls
      window.ga = blockedGa;
    },

    /**
     * Disable Facebook Pixel
     */
    disableFacebookPixel() {
      // Disable fbq. The real one is kept so a same-page accept can restore it
      // (restoreMarketingTracking); skipped when fbq is already our stub, or a
      // repeat block would overwrite the saved original with the stub.
      const fbqIsReal = typeof window.fbq === 'function' && window.fbq !== blockedFbq;
      if (fbqIsReal) {
        window.fbq('consent', 'revoke');
        this.revokedFbq = window.fbq;
      }

      // Replace fbq with no-op
      window.fbq = blockedFbq;
    },

    /**
     * Disable Yotpo tracking features (keep reviews working)
     */
    disableYotpoTracking() {
      if (window.yotpo) {
        // Disable conversion tracking but keep review display
        window.yotpo.disableConversionTracking = true;
      }

    },

    /**
     * Disable wiedersehen analytics
     */
    disableWiedersehen() {
      // Set flag to prevent wiedersehen from sending data
      window.__wiedersehenDisabled = true;

      // If wiedersehen is already loaded, try to disable it
      if (window.wiedersehen && typeof window.wiedersehen.disable === 'function') {
        window.wiedersehen.disable();
      }

    },

    /**
     * Clear tracking-related cookies
     */
    clearTrackingCookies() {
      const trackingCookies = [
        '_ga',
        '_gid',
        '_gat',
        '_fbp',
        '_fbc',
        'fr',
        '_gcl_au'
      ];

      trackingCookies.forEach(cookieName => {
        this.deleteCookie(cookieName);
      });

    },

    /**
     * Delete a cookie by name
     * @param {string} name - Cookie name
     */
    deleteCookie(name) {
      const domains = [
        window.location.hostname,
        '.' + window.location.hostname,
        window.location.hostname.replace(/^www\./, '.'),
        '.' + window.location.hostname.replace(/^www\./, '')
      ];

      domains.forEach(domain => {
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=' + domain;
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;';
      });
    },

    /**
     * Check if tracking is currently enabled
     * @returns {boolean}
     */
    isTrackingEnabled() {
      return this.trackingEnabled && !window.__trackingDisabled;
    },

    /**
     * Wrapper for conditional tracking calls
     * @param {Function} trackingFn - Function to call if tracking is enabled
     */
    track(trackingFn) {
      if (this.isTrackingEnabled()) {
        try {
          trackingFn();
        } catch (e) {
          console.error('[TrackingManager] Error in tracking call:', e);
        }
      }
    }
  };

  // Native consent gating starts NOW, not on DOMContentLoaded: it needs no
  // DOM, and Google's wait_for_update window opens when gtag.js loads, which
  // can be well before DOMContentLoaded on this theme. PrivacyOptOut has
  // already initialized (synchronous <head> script above this one).
  TrackingManager.initNativeConsent();

  // Auto-initialize the rest on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => TrackingManager.init());
  } else {
    TrackingManager.init();
  }

  // Expose to global scope
  window.TrackingManager = TrackingManager;
})();
